import React, { useState, useEffect, useMemo } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, ProgressBar, Badge } from 'react-bootstrap';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../services/api';
import { DashboardStats, Activity } from '../types';

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsData, activitiesData] = await Promise.all([
          dashboardAPI.getStats(),
          dashboardAPI.getRecentActivity()
        ]);
        
        setStats(statsData);
        setActivities(activitiesData);
      } catch (error) {
        setError('Failed to load dashboard data');
        console.error('Dashboard error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  // Helpers
  const pct = (part?: number, whole?: number) => {
    const p = Number(part);
    const w = Number(whole);
    if (!w || !isFinite(w) || w <= 0) return 0;
    if (!isFinite(p) || p < 0) return 0;
    const v = Math.round((p / w) * 100);
    return Math.min(100, Math.max(0, v));
  };

  const timeAgo = (d?: Date) => {
    if (!d) return '';
    const ts = typeof d === 'string' ? new Date(d) : d;
    const diff = Date.now() - ts.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    const days = Math.floor(hrs / 24);
    return `${days}d ago`;
  };

  if (loading) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
        <Spinner animation="border" role="status" style={{ width: '3rem', height: '3rem', color: '#667eea' }}>
          <span className="visually-hidden">Loading...</span>
        </Spinner>
        <p className="mt-3 text-muted">Loading dashboard...</p>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="py-4">
        <Alert variant="danger" className="modern-card">{error}</Alert>
      </Container>
    );
  }

  const renderSuperAdminStats = () => (
    <>
      {/* Hero */}
      <Row className="g-4 mb-4">
        <Col>
          <Card className="border-0 shadow-lg" style={{ 
            background: 'linear-gradient(135deg, #0F172A 0%, #1E3A8A 50%, #E63946 100%)', 
            color: '#fff',
            borderRadius: '20px'
          }}>
            <Card.Body className="p-4 p-md-5 d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
              <div>
                <div className="text-uppercase small opacity-75">Overview</div>
                <h3 className="mb-1">Welcome back, {user?.name}</h3>
                <div className="opacity-75">Here’s what’s happening across all customers</div>
              </div>
              <div className="d-flex gap-3 mt-3 mt-md-0">
                <div className="text-center">
                  <div className="fw-bold" style={{ fontSize: 24 }}>{stats?.totalCustomers ?? 0}</div>
                  <div className="small opacity-75">Customers</div>
                </div>
                <div className="text-center">
                  <div className="fw-bold" style={{ fontSize: 24 }}>{stats?.totalGateways ?? 0}</div>
                  <div className="small opacity-75">Gateways</div>
                </div>
                <div className="text-center">
                  <div className="fw-bold" style={{ fontSize: 24 }}>{stats?.totalSystems ?? 0}</div>
                  <div className="small opacity-75">Systems</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* KPI cards */}
      <Row className="row-cols-1 row-cols-sm-2 row-cols-lg-4 g-4 mb-4">
        <Col>
          <Card className="stat-card stat-card-primary h-100 border-0 shadow" style={{ borderRadius: '16px' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <div className="text-muted small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Total</div>
                  <div className="fw-bold" style={{ fontSize: '1.1rem' }}>Customers</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                  width: 48, 
                  height: 48, 
                  background: 'linear-gradient(135deg, #E63946 0%, #DC2626 100%)',
                  boxShadow: '0 4px 12px rgba(230, 57, 70, 0.4)'
                }}>
                  <span className="text-white fw-bold" style={{ fontSize: '1.25rem' }}>{stats?.totalCustomers ?? 0}</span>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-success small">● {stats?.activeCustomers ?? 0} active</span>
                <span className="text-danger small">● {stats?.inactiveCustomers ?? 0} inactive</span>
              </div>
              <div style={{ height: 8, background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ 
                  width: `${pct(stats?.activeCustomers, stats?.totalCustomers)}%`,
                  height: '100%', 
                  background: 'linear-gradient(90deg, #E63946 0%, #DC2626 100%)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="stat-card stat-card-success h-100 border-0 shadow" style={{ borderRadius: '16px' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <div className="text-muted small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Total</div>
                  <div className="fw-bold" style={{ fontSize: '1.1rem' }}>Users</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                  width: 48, 
                  height: 48, 
                  background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                  boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
                }}>
                  <span className="text-white fw-bold" style={{ fontSize: '1.25rem' }}>{stats?.totalUsers ?? 0}</span>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-success small">● {stats?.activeUsers ?? 0} active</span>
                <span className="text-danger small">● {stats?.inactiveUsers ?? 0} inactive</span>
              </div>
              <div style={{ height: 8, background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ 
                  width: `${pct(stats?.activeUsers, stats?.totalUsers)}%`,
                  height: '100%', 
                  background: 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="stat-card stat-card-info h-100 border-0 shadow" style={{ borderRadius: '16px' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <div className="text-muted small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Total</div>
                  <div className="fw-bold" style={{ fontSize: '1.1rem' }}>Gateways</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                  width: 48, 
                  height: 48, 
                  background: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)',
                  boxShadow: '0 4px 12px rgba(30, 58, 138, 0.4)'
                }}>
                  <span className="text-white fw-bold" style={{ fontSize: '1.25rem' }}>{stats?.totalGateways ?? 0}</span>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-success small">● {stats?.onlineGateways ?? 0} online</span>
                <span className="text-info small">● {stats?.activeGateways ?? 0} active</span>
              </div>
              <div style={{ height: 8, background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ 
                  width: `${pct(stats?.onlineGateways, stats?.totalGateways)}%`,
                  height: '100%', 
                  background: 'linear-gradient(90deg, #1E3A8A 0%, #3B82F6 100%)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="stat-card stat-card-warning h-100 border-0 shadow" style={{ borderRadius: '16px' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <div className="text-muted small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Attention</div>
                  <div className="fw-bold" style={{ fontSize: '1.1rem' }}>AMC Expiring</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                  width: 48, 
                  height: 48, 
                  background: 'linear-gradient(135deg, #f59e0b 0%, #f97316 100%)',
                  boxShadow: '0 4px 12px rgba(245, 158, 11, 0.4)'
                }}>
                  <span className="text-white fw-bold" style={{ fontSize: '1.25rem' }}>{stats?.expiringThisMonth ?? 0}</span>
                </div>
              </div>
              <div className="text-muted small">⏰ Contracts expiring this month</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="row-cols-1 row-cols-sm-2 row-cols-lg-3 g-4 mb-4">
        <Col>
          <Card className="stat-card stat-card-danger h-100 border-0 shadow" style={{ borderRadius: '16px' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <div className="text-muted small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Alert</div>
                  <div className="fw-bold" style={{ fontSize: '1.1rem' }}>AMC Expired</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                  width: 48, 
                  height: 48, 
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
                }}>
                  <span className="text-white fw-bold" style={{ fontSize: '1.25rem' }}>{stats?.expiredAMC ?? 0}</span>
                </div>
              </div>
              <div className="text-danger small">⚠️ Requires immediate attention</div>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="modern-card h-100 border-0 shadow" style={{ borderRadius: '16px', background: 'linear-gradient(135deg, #E6394615 0%, #DC262615 100%)' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <div className="text-muted small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Catalog</div>
                  <div className="fw-bold" style={{ fontSize: '1.1rem' }}>Products</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                  width: 48, 
                  height: 48, 
                  background: 'linear-gradient(135deg, #E63946 0%, #DC2626 100%)',
                  boxShadow: '0 4px 12px rgba(230, 57, 70, 0.3)'
                }}>
                  <span className="text-white fw-bold" style={{ fontSize: '1.25rem' }}>{stats?.totalProducts ?? 0}</span>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-success small">● {stats?.activeProducts ?? 0} active</span>
              </div>
              <div style={{ height: 8, background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ 
                  width: `${pct(stats?.activeProducts, stats?.totalProducts)}%`,
                  height: '100%', 
                  background: 'linear-gradient(90deg, #E63946 0%, #DC2626 100%)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="stat-card stat-card-info h-100 border-0 shadow" style={{ borderRadius: '16px' }}>
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start mb-3">
                <div>
                  <div className="text-muted small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Deployed</div>
                  <div className="fw-bold" style={{ fontSize: '1.1rem' }}>Systems</div>
                </div>
                <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                  width: 48, 
                  height: 48, 
                  background: 'linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)',
                  boxShadow: '0 4px 12px rgba(30, 58, 138, 0.4)'
                }}>
                  <span className="text-white fw-bold" style={{ fontSize: '1.25rem' }}>{stats?.totalSystems ?? 0}</span>
                </div>
              </div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span className="text-success small">● {stats?.activeSystems ?? 0} active</span>
              </div>
              <div style={{ height: 8, background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                <div style={{ 
                  width: `${pct(stats?.activeSystems, stats?.totalSystems)}%`,
                  height: '100%', 
                  background: 'linear-gradient(90deg, #1E3A8A 0%, #3B82F6 100%)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  const renderCustomerStats = () => (
    <>
      {/* Hero */}
      <Row className="g-3 mb-3">
        <Col>
          <Card className="border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #0d47a1, #1976d2)', color: '#fff' }}>
            <Card.Body className="d-flex flex-column flex-md-row align-items-start align-items-md-center justify-content-between">
              <div>
                <div className="text-uppercase small opacity-75">Overview</div>
                <h3 className="mb-1">Welcome back, {user?.name}</h3>
                <div className="opacity-75">Here’s your organization snapshot</div>
              </div>
              <div className="d-flex gap-3 mt-3 mt-md-0">
                <div className="text-center">
                  <div className="fw-bold" style={{ fontSize: 24 }}>{stats?.totalSystems ?? 0}</div>
                  <div className="small opacity-75">Systems</div>
                </div>
                <div className="text-center">
                  <div className="fw-bold" style={{ fontSize: 24 }}>{stats?.totalUsers ?? 0}</div>
                  <div className="small opacity-75">Users</div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Flexible grid: only render cards that have data */}
      <Row className="row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 g-4 mb-4">
        {!!(stats?.totalUsers ?? 0) && (
          <Col>
            <Card className="h-100 border-0 shadow" style={{ borderRadius: '16px' }}>
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <div className="text-muted small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Total</div>
                    <div className="fw-bold" style={{ fontSize: '1.1rem' }}>Users</div>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                    width: 40, 
                    height: 40, 
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    boxShadow: '0 4px 12px rgba(17, 153, 142, 0.3)'
                  }}>
                    <span className="text-white fw-bold">{stats?.totalUsers ?? 0}</span>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-success small">● {stats?.activeUsers ?? 0} active</span>
                  <span className="text-danger small">● {stats?.inactiveUsers ?? 0} inactive</span>
                </div>
                <div style={{ height: 6, background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ 
                    width: `${pct(stats?.activeUsers, stats?.totalUsers)}%`,
                    height: '100%', 
                    background: 'linear-gradient(90deg, #11998e 0%, #38ef7d 100%)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {!!(stats?.totalSystems ?? 0) && (
          <Col>
            <Card className="h-100 border-0 shadow" style={{ borderRadius: '16px' }}>
              <Card.Body className="p-4">
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <div className="text-muted small mb-1" style={{ fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px' }}>Deployed</div>
                    <div className="fw-bold" style={{ fontSize: '1.1rem' }}>Systems</div>
                  </div>
                  <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ 
                    width: 40, 
                    height: 40, 
                    background: 'linear-gradient(135deg, #3a7bd5 0%, #00d2ff 100%)',
                    boxShadow: '0 4px 12px rgba(58, 123, 213, 0.3)'
                  }}>
                    <span className="text-white fw-bold">{stats?.totalSystems ?? 0}</span>
                  </div>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-success small">● {stats?.onlineSystems ?? 0} online</span>
                  <span className="text-info small">● {stats?.activeSystems ?? 0} active</span>
                </div>
                <div style={{ height: 6, background: '#e9ecef', borderRadius: '10px', overflow: 'hidden' }}>
                  <div style={{ 
                    width: `${pct(stats?.onlineSystems, stats?.totalSystems)}%`,
                    height: '100%', 
                    background: 'linear-gradient(90deg, #3a7bd5 0%, #00d2ff 100%)',
                    transition: 'width 0.3s ease'
                  }} />
                </div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {!!(stats?.amcStatus) && (
          <Col>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <div className="fw-semibold">AMC Status</div>
                  <Badge bg={stats?.amcStatus === 'active' ? 'success' : stats?.amcStatus === 'expired' ? 'danger' : 'warning'} text={stats?.amcStatus === 'active' ? undefined : 'dark'}>
                    {(stats?.amcStatus || 'unknown').toUpperCase()}
                  </Badge>
                </div>
                <div className="text-muted small">Ends {stats?.amcEndDate ? new Date(stats.amcEndDate).toLocaleDateString() : '—'}</div>
              </Card.Body>
            </Card>
          </Col>
        )}

        {!!(stats?.companyName) && (
          <Col>
            <Card className="h-100 shadow-sm">
              <Card.Body>
                <div className="fw-semibold mb-1">Company</div>
                <div className="h5 mb-1">{stats?.companyName}</div>
                <div className="text-muted small">Your organization</div>
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>
    </>
  );

  const renderUserStats = () => (
    <>
      {/* Hero */}
      <Row className="g-3 mb-3">
        <Col>
          <Card className="border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #0d47a1, #1976d2)', color: '#fff' }}>
            <Card.Body>
              <div className="text-uppercase small opacity-75">Overview</div>
              <h3 className="mb-1">Welcome back, {user?.name}</h3>
              <div className="opacity-75">Your account summary</div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="row-cols-1 row-cols-sm-2 g-3 mb-3">
        <Col>
          <Card className="h-100 shadow-sm">
            <Card.Body className="text-center">
              <h3 className="text-info">{stats?.companyName}</h3>
              <p className="mb-0">Company</p>
              <small className="text-muted">Your organization</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Row className="row-cols-1 row-cols-md-2 g-3 mb-3">
        <Col>
          <Card className="h-100 shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Profile Information</h5>
            </Card.Header>
            <Card.Body>
              <p><strong>Name:</strong> {stats?.name}</p>
              <p><strong>Designation:</strong> {stats?.designation || 'Not specified'}</p>
              <p><strong>Department:</strong> {stats?.department || 'Not specified'}</p>
              <p><strong>Status:</strong> 
                <span className={`badge bg-${stats?.accountStatus === 'Active' ? 'success' : 'danger'} ms-2`}>
                  {stats?.accountStatus}
                </span>
              </p>
            </Card.Body>
          </Card>
        </Col>
        <Col>
          <Card className="h-100 shadow-sm">
            <Card.Header>
              <h5 className="mb-0">Account Activity</h5>
            </Card.Header>
            <Card.Body>
              <p><strong>Last Login:</strong> {
                stats?.lastLogin 
                  ? new Date(stats.lastLogin).toLocaleString()
                  : 'No login recorded'
              }</p>
              <p><strong>Company:</strong> {stats?.companyName || 'Not specified'}</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <Container fluid className="py-3">
      <div style={{ width: '80%', margin: '0 auto' }}>
        <Row>
          <Col>
          {user?.role === 'superadmin' && renderSuperAdminStats()}
          {(user?.role === 'customer' || user?.role === 'user') && renderCustomerStats()}
          
          {activities.length > 0 && (
            <Row>
              <Col xs={12}>
                <Card className="modern-card">
                  <Card.Header className="gradient-card-header d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">Recent Activity</h5>
                    <span className="badge-gradient badge-gradient-primary">{activities.length}</span>
                  </Card.Header>
                  <Card.Body>
                    {activities.length === 0 ? (
                      <p className="text-muted">No recent activity</p>
                    ) : (
                      <div className="list-group list-group-flush">
                        {activities.map((activity, index) => (
                          <div key={index} className="list-group-item border-0 px-0 py-3">
                            <div className="d-flex justify-content-between align-items-start gap-3">
                              <div>
                                <h6 className="mb-1 fw-semibold">{activity.title}</h6>
                                <p className="mb-1 text-muted">{activity.subtitle}</p>
                              </div>
                              <div className="text-end">
                                <div className="text-muted small">{new Date(activity.timestamp).toLocaleString()}</div>
                                <div className="text-muted small">{timeAgo(activity.timestamp as any)}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
          </Col>
        </Row>
      </div>
    </Container>
  );
};

export default Dashboard;